import React, { useEffect, useState } from 'react';
import { api } from '../../api';
import { getDeviceId, setStation } from '../../lib/posStation';

// Full-screen "Which station is this iPad?" picker. Shown after PIN entry
// on any iPad that hasn't been assigned a station yet, and again anytime
// staff hits "Change station" in POS Settings.
export default function PosStationPicker({ onPicked }) {
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [picking, setPicking] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .listPosStations(getDeviceId())
      .then((rows) => {
        if (cancelled) return;
        setStations(Array.isArray(rows) ? rows : []);
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setErr(e.message || 'Failed to load stations');
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const handlePick = async (row) => {
    if (row.occupied || picking) return;
    setPicking(true);
    setErr('');
    try {
      await api.claimPosStation(row.station_key, getDeviceId());
      setStation(row.station_key, row.name);
      onPicked(row);
    } catch (e) {
      setErr(e.message || 'Could not take that station');
      try {
        const rows = await api.listPosStations(getDeviceId());
        setStations(Array.isArray(rows) ? rows : []);
      } catch {
        /* keep current list */
      }
    } finally {
      setPicking(false);
    }
  };

  return (
    <div className="pos-station-picker">
      <div className="pos-station-picker-card">
        <h1 className="pos-station-picker-title">Which station is this iPad?</h1>
        <p className="pos-station-picker-sub">
          Each station is one till — one terminal and one docket printer.
          A station already in use on another iPad is greyed out.
        </p>

        {loading && <div className="pos-station-picker-loading">Loading stations…</div>}

        {!loading && err && (
          <div className="pos-station-picker-error">
            {err}
            <div style={{ marginTop: 8, fontSize: 14, opacity: 0.85 }}>
              Ask an admin to add at least one station in Settings → Counter stations,
              or release a stuck station there.
            </div>
          </div>
        )}

        {!loading && !err && stations.length === 0 && (
          <div className="pos-station-picker-error">
            No stations configured yet.
            <div style={{ marginTop: 8, fontSize: 14, opacity: 0.85 }}>
              Open the admin panel and add one under Settings → Counter stations.
            </div>
          </div>
        )}

        {!loading && stations.length > 0 && (
          <div className="pos-station-picker-grid">
            {stations.map((s) => (
              <button
                key={s.id}
                type="button"
                className={`pos-station-picker-btn${s.occupied ? ' is-taken' : ''}`}
                disabled={s.occupied || picking}
                onClick={() => handlePick(s)}
              >
                <span className="pos-station-picker-btn-name">{s.name}</span>
                <span className="pos-station-picker-btn-key">
                  {s.occupied ? 'In use on another iPad' : s.station_key}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
